import { useState, useEffect, useRef } from "react";
import { useConfirm } from "@/components/app/ConfirmDialog";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Sparkles, Loader2, ShieldCheck, Pencil, AlertCircle, PanelLeft, X, Layers,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { StudioComposer } from "@/components/app/StudioComposer";
import { ScenePanel } from "@/components/app/ScenePanel";
import { SessionList, type SessionListItem } from "@/components/app/SessionList";
import { findMismatches, type ConfigLink } from "@/lib/sceneCompatibility";
import { SceneStartDialog } from "@/components/app/SceneStartDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ImageLightbox } from "@/components/app/ImageLightbox";
import { ImageActionMenu } from "@/components/app/ImageActionMenu";
import {
  useStudioSession, createStudioSession,
  UNTITLED_SESSION,
  type StudioAttachment, type StudioSettings,
} from "@/hooks/useStudioSession";
import { useGenerationCosts } from "@/hooks/useGenerationCosts";
import * as Sentry from "@sentry/react";




/**
 * Drop answers whose field is no longer visible.
 *
 * Changing a parent answer hides its dependent fields, but their answers would
 * otherwise linger — showing a misleading answered count, and reappearing if
 * the parent were switched back. The prompt builder ignores hidden answers
 * regardless; this keeps the visible state honest too.
 */
function pruneHiddenAnswers(
  briefConfig: any,
  answers: Record<string, string>,
): Record<string, string> {
  const satisfied = (cond: any) =>
    !cond || (Array.isArray(cond.values) && cond.values.includes(answers[cond.field] || ""));

  const next = { ...answers };
  for (const step of (briefConfig?.steps || []) as any[]) {
    const stepVisible = satisfied(step?.showWhen);
    if (Array.isArray(step?.fields)) {
      for (const f of step.fields) {
        if (!stepVisible || !satisfied(f?.showWhen)) delete next[f.key];
      }
    } else if (step?.fieldKey && !stepVisible) {
      delete next[step.fieldKey];
    }
  }
  return next;
}

/** Credit action key for the chosen output size. */
function costKeyForSize(size: string): string {
  if (size === "0.5K") return "studio_0_5k";
  if (size === "2K") return "studio_2k";
  if (size === "4K") return "studio_4k";
  return "studio_1k";
}

export default function StudioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { workspace, unlockedRegions } = useWorkspace();
  const { user } = useAuth();
  const { costOf } = useGenerationCosts();

  const sessionId = searchParams.get("session");
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [cameras, setCameras] = useState<{ id: string; name: string }[]>([]);
  const [products, setProducts] = useState<
    { id: string; name: string; configId?: string | null; thumbnailUrl?: string | null }[]
  >([]);
  const [briefs, setBriefs] = useState<{ id: string; name: string; regionKey: string | null; config: any }[]>([]);
  const [lightbox, setLightbox] = useState<{ url: string; id: string } | null>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const [approved, setApproved] = useState<Set<string>>(new Set());
  const [showSessions, setShowSessions] = useState(true);
  // Collapsed by default. Opening a session should show what it contains, not
  // a form; the questions are already answered by the time you get here, and
  // an expanded panel pushed the images and the composer off the screen.
  const [briefCollapsed, setBriefCollapsed] = useState(true);
  const [sceneDialogOpen, setSceneDialogOpen] = useState(false);
  // Loaded once here rather than per dialog opening: the composer needs the
  // same rows to notice a pairing that has stopped fitting.
  const [configLinks, setConfigLinks] = useState<ConfigLink[]>([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const historyRef = useRef<HTMLDivElement>(null);

  const studio = useStudioSession(sessionId);

  // Switching sessions collapses the panel again — the expanded state belongs
  // to the session you were editing, not the one you just opened.
  useEffect(() => { setBriefCollapsed(true); }, [sessionId]);

  // ── Load sessions, cameras, products ──────────────────────────────────
  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    (async () => {
      const [sessionRes, cameraRes, productRes, briefRes] = await Promise.all([
        supabase.from("studio_sessions" as any)
          .select("id, name, updated_at")
          .eq("workspace_id", workspace.id)
          .eq("is_archived", false)
          .order("updated_at", { ascending: false })
          .limit(50),
        supabase.from("wizard_configs")
          .select("id, name")
          .eq("type", "photographer")
          .order("name"),
        supabase.from("products")
          // product_params._config_id says which product type this is, which
          // is what config_links keys scenes off.
          .select("id, name, product_params")
          .eq("workspace_id", workspace.id)
          .order("name"),
        // Availability rules for a brief preset: assigned to this
        // workspace, and either global or in an unlocked region. Studio needs
        // no region concept of its own — the brief carries it.
        supabase.from("workspace_wizard_configs")
          .select("wizard_configs!inner(id, name, region_key, config)")
          .eq("workspace_id", workspace.id)
          .eq("wizard_configs.type", "brief"),
      ]);
      if (cancelled) return;
      setSessions(((sessionRes.data as any[]) || []) as SessionListItem[]);
      setCameras(((cameraRes.data as any[]) || []).map((c) => ({ id: c.id, name: c.name })));
      const productRows = (productRes.data as any[]) || [];
      setProducts(productRows.map((p) => ({
        id: p.id, name: p.name, configId: (p.product_params as any)?._config_id || null,
      })));

      // Thumbnails come second so the pickers are usable immediately rather
      // than waiting on a second round trip.
      if (productRows.length > 0) {
        const { data: imgs } = await supabase
          .from("product_images")
          .select("product_id, storage_path, is_primary")
          .in("product_id", productRows.map((p) => p.id))
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true });
        if (cancelled) return;
        const thumbs = new Map<string, string>();
        for (const img of imgs || []) {
          // First row per product wins: the ordering above puts the primary
          // image first, falling back to the oldest.
          if (thumbs.has(img.product_id)) continue;
          thumbs.set(
            img.product_id,
            supabase.storage.from("product-images").getPublicUrl(img.storage_path).data.publicUrl,
          );
        }
        setProducts((prev) => prev.map((p) => ({ ...p, thumbnailUrl: thumbs.get(p.id) || null })));
      }

      {
        const { data, error } = await supabase
          .from("config_links")
          .select("brief_config_id, linked_config_id, linked_type");
        if (cancelled) return;
        if (error) {
          // Not fatal: without links nothing is narrowed and nothing is
          // flagged, which is how Studio behaved before they were read.
          console.error("Failed to load config links:", error);
          Sentry.captureException(error);
        } else {
          setConfigLinks((data as ConfigLink[]) || []);
        }
      }

      const unlockedKeys = new Set(unlockedRegions.map((r) => r.region_key));
      const rawBriefs = ((briefRes.data as any[]) || []).map((r) => r.wizard_configs);
      setBriefs(rawBriefs
        .filter((c: any) => !c.region_key || unlockedKeys.has(c.region_key))
        .map((c: any) => ({ id: c.id, name: c.name, regionKey: c.region_key, config: c.config })));

      setSessionsLoading(false);

      // Card thumbnails: newest image per session, plus a count for the meta
      // line. Fetched after the list paints so the sessions appear immediately.
      const sessionIds = ((sessionRes.data as any[]) || []).map((s) => s.id);
      if (sessionIds.length === 0) return;

      // What each session is *about*, for search. A session's name is the
      // first prompt truncated to 48 characters, so searching names alone
      // misses everything asked after the first turn — and everything a scene
      // contributed, which for a scene session is the entire request.
      //
      // Loaded after the list so the sidebar is usable immediately, and after
      // rawBriefs because it needs the scene names. Bounded by the same 50
      // sessions the list shows; this becomes a server-side search when a
      // workspace outgrows that.
      {
        const [turnRes, sessionSettingsRes] = await Promise.all([
          supabase.from("studio_turns" as any)
            .select("session_id, prompt")
            .in("session_id", sessionIds)
            .not("prompt", "is", null),
          supabase.from("studio_sessions" as any)
            .select("id, settings")
            .in("id", sessionIds),
        ]);
        if (cancelled) return;

        const text = new Map<string, string[]>();
        const push = (id: string, value?: string | null) => {
          if (!value) return;
          const list = text.get(id) || [];
          list.push(value);
          text.set(id, list);
        };

        for (const t of ((turnRes.data as any[]) || [])) push(t.session_id, t.prompt);

        // The scene and its answers. Answer values are stored as keys
        // ("warm_hygge"), so underscores become spaces — otherwise searching
        // "warm hygge" finds nothing.
        const sceneNames = new Map(rawBriefs.map((b: any) => [b.id, b.name]));
        for (const row of ((sessionSettingsRes.data as any[]) || [])) {
          const cfg = (row.settings || {}) as Record<string, any>;
          push(row.id, sceneNames.get(cfg.briefConfigId) as string | undefined);
          for (const v of Object.values(cfg.briefAnswers || {})) {
            if (typeof v === "string") push(row.id, v.replace(/_/g, " "));
          }
        }

        setSessions((prev) => prev.map((x) => ({
          ...x,
          searchText: (text.get(x.id) || []).join(" \n "),
        })));
      }

      const { data: sessionVisuals } = await supabase
        .from("visuals")
        .select("session_id, storage_path")
        .in("session_id", sessionIds)
        .not("storage_path", "is", null)
        .order("created_at", { ascending: false });
      if (cancelled) return;

      const thumbs = new Map<string, string>();
      const counts = new Map<string, number>();
      for (const v of (sessionVisuals as any[]) || []) {
        counts.set(v.session_id, (counts.get(v.session_id) || 0) + 1);
        if (!thumbs.has(v.session_id)) {
          const { data } = supabase.storage.from("renders").getPublicUrl(v.storage_path);
          thumbs.set(v.session_id, data.publicUrl);
        }
      }
      setSessions((prev) =>
        prev.map((s) => ({
          ...s,
          thumbnailUrl: thumbs.get(s.id) || null,
          visualCount: counts.get(s.id) || 0,
        })),
      );
    })();
    return () => { cancelled = true; };
  }, [workspace?.id, unlockedRegions]);

  // Keep the newest turn in view as generation completes.
  useEffect(() => {
    historyRef.current?.scrollTo({ top: historyRef.current.scrollHeight, behavior: "smooth" });
  }, [studio.turns.length, studio.isGenerating]);

  // Approval lives on the visual (`is_master`), so seed it from the database.
  // It was only ever set by clicking Approve in this session, which meant a
  // reopened session showed every image as unapproved.
  const outputIdKey = studio.turns
    .flatMap((t: any) => (t.outputs || []).map((o: any) => o.id))
    .filter(Boolean)
    .join(",");
  useEffect(() => {
    const ids = outputIdKey ? outputIdKey.split(",") : [];
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("visuals")
        .select("id")
        .in("id", ids)
        .eq("is_master", true);
      if (cancelled || !data) return;
      setApproved(new Set((data as any[]).map((v) => v.id)));
    })();
    return () => { cancelled = true; };
  }, [outputIdKey]);

  const startSession = async (initialSettings?: Partial<StudioSettings>, name?: string) => {
    if (!workspace || !user) return;
    const id = await createStudioSession(workspace.id, user.id, initialSettings, name);
    if (!id) return;
    setSessions((prev) => [
      { id, name: name || UNTITLED_SESSION, updated_at: new Date().toISOString(), thumbnailUrl: null, visualCount: 0 },
      ...prev,
    ]);
    setSearchParams({ session: id });
  };

  /**
   * "Continue in Studio" from the product editor arrives as ?product=<id>.
   * Open a session with that product already attached, rather than dropping
   * the user on the empty state and making them pick it again.
   */
  const productParam = searchParams.get("product");
  useEffect(() => {
    if (!productParam || !workspace || !user) return;
    let cancelled = false;
    (async () => {
      const id = await createStudioSession(workspace.id, user.id, { productId: productParam });
      if (cancelled || !id) return;
      // replace: Back should return to the product, not to this same URL.
      setSearchParams({ session: id }, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productParam, workspace?.id, user?.id]);

  /** Save the edited name, keeping the list in sync. Empty input cancels. */
  const commitRename = () => {
    const next = renameValue.trim();
    setIsRenaming(false);
    if (!next || next === studio.sessionName) return;
    void studio.renameSession(next);
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, name: next } : s)));
  };

  /**
   * Delete a session, keeping everything it generated.
   *
   * The images are the output; the session is only the workspace they were
   * made in. `visuals.session_id` is ON DELETE SET NULL, so the visuals detach
   * and stay in the asset library — nothing here touches them. The prompt says
   * so, because "delete" otherwise reads as taking the work with it.
   */
  const deleteSession = async (target: SessionListItem) => {
    if (deletingSessionId) return;

    const { count } = await supabase
      .from("visuals")
      .select("id", { count: "exact", head: true })
      .eq("session_id", target.id);

    const kept = count
      ? `Its ${count} image${count === 1 ? "" : "s"} stay in your assets.`
      : "";
    const ok = await confirm({
      title: `Delete the session "${target.name}"?`,
      description: kept || undefined,
      confirmLabel: "Delete session",
    });
    if (!ok) return;

    setDeletingSessionId(target.id);
    try {
      // `.select()` so we can tell a real delete from one that matched no
      // rows. Without it an RLS denial returns success having changed nothing,
      // the row vanishes from local state, and it reappears on the next load —
      // which looks exactly like "delete does nothing".
      const { data, error } = await supabase
        .from("studio_sessions" as any)
        .delete()
        .eq("id", target.id)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(`Session ${target.id} matched no rows on delete`);
      }

      setSessions((prev) => prev.filter((s) => s.id !== target.id));
      // Deleting the session that is open would leave the canvas pointing at a
      // row that no longer exists, so fall back to the empty state.
      if (sessionId === target.id) setSearchParams({});
    } catch (err) {
      console.error("Failed to delete session:", err);
      Sentry.captureException(err);
      toast.error("Could not delete this session. Please try again.");
    } finally {
      setDeletingSessionId(null);
    }
  };

  const handleSubmit = (prompt: string, attachments: StudioAttachment[]) => {
    void studio.generate(prompt, attachments);
  };

  /**
   * Approve marks the visual as a master, which is what makes it available in
   * the editor for format variants.
   */
  const approve = async (visualId: string) => {
    setApproving(visualId);
    try {
      const { error } = await supabase
        .from("visuals")
        .update({ is_master: true } as any)
        .eq("id", visualId);
      if (error) throw error;
      setApproved((prev) => new Set(prev).add(visualId));
    } catch (err) {
      console.error("Approve failed:", err);
      Sentry.captureException(err);
    } finally {
      setApproving(null);
    }
  };

  const openInEditor = (visualId: string, url: string) => {
    const from = `/app/studio?session=${sessionId}`;
    navigate(`/app/edit-visual?v=${visualId}&from=${encodeURIComponent(from)}`, {
      state: {
        imageUrl: url,
        allVersionUrls: [url],
        // Carry the session's product through so format variants regenerate
        // with the product photography as reference, not from the image alone.
        productImageUrls: sessionProductImages,
        productInfo: sessionProductInfo,
        renderId: visualId,
        parentRenderId: visualId,
        // No briefId: Re-generate re-runs a brief, and a Studio visual has
        // none. EditVisualPage hides the action rather than offering a no-op.
        briefId: null,
        // Label and return path so the editor reflects where we came from
        // instead of defaulting to generic chrome.
        sessionName: studio.sessionName,
        returnTo: `/app/studio?session=${sessionId}`,
      },
    });
  };

  // The session's attached product, resolved for the editor handoff so
  // format variants keep product fidelity.
  const [sessionProductImages, setSessionProductImages] = useState<string[]>([]);
  const [sessionProductInfo, setSessionProductInfo] = useState<any>(null);
  useEffect(() => {
    const productId = studio.settings.productId;
    if (!productId) { setSessionProductImages([]); setSessionProductInfo(null); return; }
    let cancelled = false;
    (async () => {
      const [imgRes, prodRes] = await Promise.all([
        supabase.from("product_images").select("storage_path").eq("product_id", productId).limit(4),
        supabase.from("products").select("name, product_params").eq("id", productId).maybeSingle(),
      ]);
      if (cancelled) return;
      setSessionProductImages(((imgRes.data as any[]) || []).map((i) =>
        supabase.storage.from("product-images").getPublicUrl(i.storage_path).data.publicUrl));
      const p = prodRes.data as any;
      setSessionProductInfo(p ? { name: p.name, ...(p.product_params || {}) } : null);
    })();
    return () => { cancelled = true; };
  }, [studio.settings.productId]);

  const activeBrief = briefs.find((b) => b.id === studio.settings.briefConfigId) || null;

  const creditsPerImage = costOf(costKeyForSize(studio.settings.resolution), 20);

  if (!studio.config.enabled) {
    return (
      <div className="max-w-lg mx-auto mt-20 text-center space-y-3">
        <Sparkles className="w-10 h-10 mx-auto text-muted-foreground/40" />
        <h1 className="font-display font-semibold text-lg">Studio isn’t enabled</h1>
        <p className="text-sm text-muted-foreground">
          Studio is turned off for this workspace. An administrator can enable it in settings.
        </p>
      </div>
    );
  }

  /** Open a session already set up as a scene, so nothing has to be re-picked. */
  const startScene = async ({ sceneId, productId, cameraId, answers }: {
    sceneId: string; productId: string; cameraId: string; answers: Record<string, string>;
  }) => {
    setSceneDialogOpen(false);
    // The questions were just answered in the dialog, so the panel opens
    // collapsed — as a summary of what was chosen, not a form to fill again.
    setBriefCollapsed(true);
    // With a session open the scene is set up on it. Opening a second session
    // would strand whatever is already in this one.
    if (sessionId) {
      await studio.updateSettings({
        briefConfigId: sceneId, productId, cameraConfigId: cameraId, briefAnswers: answers,
      });
      return;
    }
    // A scene session knows what it is before a single prompt is typed, so it
    // is named at creation rather than waiting for the first generation.
    const sceneName = briefs.find((b) => b.id === sceneId)?.name;
    const productName = products.find((p) => p.id === productId)?.name;
    const name = [productName, sceneName].filter(Boolean).join(" · ") || undefined;
    await startSession(
      { briefConfigId: sceneId, productId, cameraConfigId: cameraId, briefAnswers: answers },
      name,
    );
  };

  return (
    <div className="flex gap-4 h-full min-h-[32rem]">
      <SceneStartDialog
        open={sceneDialogOpen}
        onOpenChange={setSceneDialogOpen}
        scenes={briefs}
        products={products}
        cameras={cameras}
        links={configLinks}
        initialProductId={studio.settings.productId}
        mode={sessionId ? "attach" : "start"}
        onStart={startScene}
      />
      {/* Sessions — hidden until a session is open, so the empty state can
          centre on the page rather than within a column. */}
      {showSessions && sessionId && (
        <aside className="w-56 shrink-0 flex flex-col gap-2">
          {/* The same two ways in as the empty state. This button used to
              call startSession() directly, so starting a second session
              silently skipped the choice and always gave you a free prompt. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gradient-primary text-primary-foreground w-full" size="sm">
                <Plus className="w-4 h-4 mr-1.5" />
                New session
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem
                onClick={() => setSceneDialogOpen(true)}
                disabled={briefs.length === 0 || products.length === 0 || cameras.length === 0}
                className="gap-2 cursor-pointer items-start py-2"
              >
                <Layers className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span className="flex flex-col">
                  <span className="text-sm">Start from a scene</span>
                  <span className="text-[11px] text-muted-foreground leading-snug">
                    Guided — needs a product and a camera style
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => void startSession()}
                className="gap-2 cursor-pointer items-start py-2"
              >
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <span className="flex flex-col">
                  <span className="text-sm">Start from a prompt</span>
                  <span className="text-[11px] text-muted-foreground leading-snug">
                    Describe it in your own words
                  </span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <SessionList
            sessions={sessions}
            activeId={sessionId}
            deletingId={deletingSessionId}
            loading={sessionsLoading}
            onOpen={(id) => setSearchParams({ session: id })}
            onDelete={(sess) => void deleteSession(sess)}
            emptyMessage="No sessions yet. Start one to begin."
          />
        </aside>
      )}

      {/* Canvas */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setShowSessions((v) => !v)}
            aria-label="Toggle sessions"
          >
            <PanelLeft className="w-4 h-4" />
          </Button>
          {sessionId ? (
            isRenaming ? (
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                  if (e.key === "Escape") { setIsRenaming(false); }
                }}
                className="text-sm font-medium bg-transparent border-b border-primary focus:outline-none px-0.5 min-w-[12rem]"
                aria-label="Session name"
              />
            ) : (
              <button
                className="text-sm font-medium hover:underline decoration-dotted underline-offset-4 flex items-center gap-1.5"
                onClick={() => { setRenameValue(studio.sessionName); setIsRenaming(true); }}
                title="Click to rename"
              >
                {studio.sessionName}
                <Pencil className="w-3 h-3 text-muted-foreground" />
              </button>
            )
          ) : (
            <span className="text-sm text-muted-foreground">Studio</span>
          )}
        </div>

        {!sessionId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
            <Sparkles className="w-10 h-10 text-muted-foreground/40" />
            <div>
              <h2 className="font-display font-semibold">What are you making?</h2>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Two ways in. Everything you make is kept in the session either way.
              </p>
            </div>

            {/* The choice between the two ways of working is the first thing
                to make, so it is two visible options rather than a default
                plus a dropdown nobody opens. */}
            <div className="grid sm:grid-cols-2 gap-3 w-full max-w-lg pt-1">
              <StartOption
                icon={<Layers className="w-5 h-5" />}
                title="Start from a scene"
                body="Answer a few questions and the prompt is written for you. Needs a product and a camera style."
                disabled={briefs.length === 0 || products.length === 0 || cameras.length === 0}
                disabledHint={
                  products.length === 0
                    ? "Add a product first"
                    : briefs.length === 0
                      ? "No scenes are set up for this workspace"
                      : "No camera styles are set up for this workspace"
                }
                onClick={() => setSceneDialogOpen(true)}
              />
              <StartOption
                icon={<Sparkles className="w-5 h-5" />}
                title="Start from a prompt"
                body="Describe what you want in your own words. Attach a product or references if you like."
                onClick={() => void startSession()}
              />
            </div>

            {/* Existing sessions stay reachable even though the sidebar is
                hidden on this screen. Same list, same behaviour — just wider. */}
            {sessions.length > 0 && (
              <div className="w-full max-w-md pt-6 text-left">
                <p className="pb-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Recent sessions
                </p>
                <SessionList
                  sessions={sessions}
                  deletingId={deletingSessionId}
                  onOpen={(id) => setSearchParams({ session: id })}
                  onDelete={(sess) => void deleteSession(sess)}
                  emptyMessage="No sessions yet."
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Turn history */}
            <div ref={historyRef} className="flex-1 overflow-y-auto space-y-6 pr-1 pb-4">
              {studio.loading ? (
                <div className="space-y-4">
                  <Skeleton className="h-20 w-2/3 rounded-xl" />
                  <Skeleton className="h-48 w-full rounded-xl" />
                </div>
              ) : studio.turns.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">
                    Describe what you want to create to get started.
                  </p>
                </div>
              ) : (
                studio.turns.map((turn) => (
                  <div key={turn.id} className="space-y-2.5">
                    {/* Prompt */}
                    <div className="flex justify-end">
                      <div className="max-w-[75%] rounded-2xl rounded-tr-sm bg-secondary px-3.5 py-2.5">
                        <p className="text-sm whitespace-pre-wrap">{turn.prompt}</p>
                        {turn.attachments.length > 0 && (
                          <div className="flex gap-1.5 mt-2">
                            {turn.attachments.map((a, i) => (
                              <img
                                key={i}
                                src={a.url}
                                alt={a.name}
                                className="h-10 w-10 rounded object-cover border border-border"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Outputs */}
                    {turn.pending ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating…
                      </div>
                    ) : turn.error ? (
                      <div className="flex items-start gap-2 text-sm text-destructive">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{turn.error}</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {turn.outputs.map((out) => {
                          const isApproved = approved.has(out.id);
                          return (
                            <div key={out.id} className="group relative">
                              <img
                                src={out.url}
                                alt="Generated"
                                onClick={() => setLightbox({ url: out.url, id: out.id })}
                                className="max-h-72 rounded-xl border border-border cursor-zoom-in object-contain bg-muted/30"
                              />
                              {/* Edit is a visible button, not a menu entry.
                                  Generate re-rolls the setup; only Edit works
                                  on this image's pixels — and the action that
                                  resolves that confusion should not hide
                                  behind a "…". */}
                              <div className="absolute top-2 right-2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openInEditor(out.id, out.url)}
                                  title="Edit this image — changes it, rather than generating a new one"
                                  className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-background/85 hover:bg-background backdrop-blur-sm border border-border text-xs font-medium"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  Edit
                                </button>
                                <ImageActionMenu
                                  className="bg-background/85 hover:bg-background backdrop-blur-sm h-8 w-8"
                                  actions={[
                                    {
                                      label: isApproved ? "Approved \u2713" : "Approve",
                                      icon: approving === out.id
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <ShieldCheck className={cn("w-4 h-4", isApproved && "text-primary")} />,
                                      onClick: () => { if (!isApproved) void approve(out.id); },
                                      disabled: isApproved || approving === out.id,
                                    },
                                  ]}
                                />
                              </div>

                              {/* Approval is worth seeing without hovering —
                                  the old button only showed it on hover. */}
                              {isApproved && (
                                <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-background/85 backdrop-blur-sm px-2 py-0.5 text-[10px] font-medium text-primary">
                                  <ShieldCheck className="w-3 h-3" />
                                  Approved
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {turn.model && !turn.error && (
                      <p className="text-[10px] text-muted-foreground">
                        {turn.model} · {turn.aspectRatio} · {turn.resolution}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            {studio.error && (
              <p className="text-xs text-destructive mb-2">{studio.error}</p>
            )}

            <div className="pb-6">
            {activeBrief && (
              <ScenePanel
                scene={activeBrief}
                answers={studio.settings.briefAnswers || {}}
                onChange={(key, value) =>
                  studio.updateSettings({
                    briefAnswers: pruneHiddenAnswers(
                      activeBrief.config,
                      { ...(studio.settings.briefAnswers || {}), [key]: value },
                    ),
                  })
                }
                onClear={() => studio.updateSettings({ briefConfigId: null, briefAnswers: {} })}
                collapsed={briefCollapsed}
                onToggleCollapsed={() => setBriefCollapsed((v) => !v)}
              />
            )}
            <StudioComposer
              settings={studio.settings}
              config={studio.config}
              cameras={cameras}
              products={products}
              briefs={briefs}
              onAddScene={() => setSceneDialogOpen(true)}
              links={configLinks}
              hasPreviousImage={studio.turns.some((t) => t.outputs.length > 0)}
              isGenerating={studio.isGenerating}
              creditsPerImage={creditsPerImage}
              onSettingsChange={studio.updateSettings}
              onUpload={studio.uploadAttachment}
              onSubmit={handleSubmit}
            />
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <ImageLightbox
          isOpen={!!lightbox}
          onClose={() => setLightbox(null)}
          imageUrl={lightbox.url}
        />
      )}
    </div>
  );
}

/**
 * One of the two ways into Studio. Disabled states say why rather than going
 * grey silently — "no scenes are set up" is a workspace configuration fact the
 * user cannot guess.
 */
function StartOption({
  icon, title, body, onClick, disabled, disabledHint,
}: {
  icon: JSX.Element;
  title: string;
  body: string;
  onClick: () => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledHint : undefined}
      className={cn(
        "text-left rounded-lg border border-border p-4 transition-colors",
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:border-primary/60 hover:bg-primary/[0.03]",
      )}
    >
      <span className="flex items-center gap-2 text-primary">{icon}</span>
      <span className="block font-medium text-sm mt-2">{title}</span>
      <span className="block text-xs text-muted-foreground mt-1 leading-relaxed">
        {disabled ? disabledHint : body}
      </span>
    </button>
  );
}
